// The filter mini-language shared by GI.* functions and live blocks.
//
//   "usage=Residential"            equality, case-insensitive for text
//   "usage=Residential|Retail"     "or" — true if usage is either one
//   "usage!=Retail, levels>3"      comma-separated clauses, ANDed
//   "name~tower"                   contains
//
// Deliberately not a query language. Anything more expressive belongs in a
// formula, where the user already has IF/AND/OR. `|` is the one exception —
// a multi-select filter (sheet filter, database criteria) needs SOME way to
// say "any of these", and OR across clauses would need real query-language
// grouping (parens, precedence) that a single flat AND-list doesn't have
// room for. A value containing a literal `|` cannot be matched exactly; that
// trade was made deliberately rather than inventing an escape syntax for a
// character gTables has never seen a real project field use.

import type { DataRow } from './types';

export type FilterOp = '=' | '!=' | '>' | '<' | '>=' | '<=' | '~';

export interface FilterClause {
  field: string;
  op: FilterOp;
  value: string;
}

export interface ParsedFilter {
  clauses: FilterClause[];
  errors: string[];
}

const CLAUSE_RE = /^\s*([^!=<>~]+?)\s*(!=|>=|<=|=|>|<|~)\s*(.*?)\s*$/;

export function parseFilter(input: string | null | undefined): ParsedFilter {
  const clauses: FilterClause[] = [];
  const errors: string[] = [];
  if (!input) return { clauses, errors };

  /*
   * A comma separates clauses — except when it is part of a VALUE.
   *
   * `usage=Retail, ground` is one clause about a usage whose name has a comma
   * in it; `usage=Retail, levels>3` is two. The two are told apart by whether
   * the fragment after the comma contains an operator: no operator means it
   * cannot be a clause of its own, so it belongs to the value before it.
   *
   * Without this the caller has to quote every value defensively, which is what
   * produced formulas like `"usage="""&$A19&""""` — four quotes in a row to say
   * one thing.
   */
  const fragments = splitClauses(input);
  const merged: string[] = [];
  for (const fragment of fragments) {
    if (merged.length > 0 && !hasOperator(fragment)) {
      merged[merged.length - 1] += `,${fragment}`;
      continue;
    }
    merged.push(fragment);
  }

  for (const part of merged) {
    if (!part.trim()) continue;
    const m = CLAUSE_RE.exec(part);
    if (!m) {
      errors.push(`cannot read filter "${part.trim()}" — expected field=value`);
      continue;
    }
    clauses.push({ field: m[1]!.trim(), op: m[2] as FilterOp, value: unquote(m[3]!) });
  }
  return { clauses, errors };
}

/** Outside quotes, so a quoted value's own operator does not count. */
function hasOperator(fragment: string): boolean {
  let quote: string | null = null;
  for (const ch of fragment) {
    if (quote) {
      if (ch === quote) quote = null;
      continue;
    }
    if (ch === '"' || ch === "'") {
      quote = ch;
      continue;
    }
    if ('!=<>~'.includes(ch)) return true;
  }
  return false;
}

/** Split on commas that are not inside quotes. */
function splitClauses(input: string): string[] {
  const out: string[] = [];
  let buf = '';
  let quote: string | null = null;
  for (const ch of input) {
    if (quote) {
      if (ch === quote) quote = null;
      buf += ch;
    } else if (ch === '"' || ch === "'") {
      quote = ch;
      buf += ch;
    } else if (ch === ',') {
      out.push(buf);
      buf = '';
    } else {
      buf += ch;
    }
  }
  out.push(buf);
  return out;
}

function unquote(s: string): string {
  if (s.length >= 2 && (s[0] === '"' || s[0] === "'") && s[s.length - 1] === s[0]) {
    return s.slice(1, -1);
  }
  return s;
}

export function matches(row: DataRow, clauses: readonly FilterClause[]): boolean {
  for (const c of clauses) {
    if (!matchOne(row.properties[c.field], c)) return false;
  }
  return true;
}

function matchOne(raw: unknown, clause: FilterClause): boolean {
  const { op, value } = clause;

  if (op === '~') {
    return String(raw ?? '').toLowerCase().includes(value.toLowerCase());
  }

  if (op === '=' || op === '!=') {
    // A `|`-separated value is "any of these" ("none of these" for !=) — a
    // plain value with no `|` is just the one-option case of the same test,
    // so equality is never special-cased separately from OR.
    const left = raw == null ? '' : String(raw);
    const isAny = value.split('|').some((opt) => {
      const o = opt.trim();
      const n = Number(o);
      if (o !== '' && typeof raw === 'number' && Number.isFinite(n)) return raw === n;
      // Blank matches null/undefined/"" so `usage=` finds the gaps.
      return left.toLowerCase() === o.toLowerCase();
    });
    return op === '=' ? isAny : !isAny;
  }

  const asNumber = Number(value);
  const numericTarget = value.trim() !== '' && Number.isFinite(asNumber);
  if (numericTarget && typeof raw === 'number') {
    switch (op) {
      case '>': return raw > asNumber;
      case '<': return raw < asNumber;
      case '>=': return raw >= asNumber;
      case '<=': return raw <= asNumber;
    }
  }

  const left = raw == null ? '' : String(raw);
  const l = left.toLowerCase();
  const r = value.toLowerCase();
  switch (op) {
    case '>': return l > r;
    case '<': return l < r;
    case '>=': return l >= r;
    case '<=': return l <= r;
    default: return false;
  }
}

export function applyFilter(rows: readonly DataRow[], input: string | null | undefined): {
  rows: DataRow[];
  errors: string[];
} {
  const { clauses, errors } = parseFilter(input);
  if (clauses.length === 0) return { rows: [...rows], errors };
  return { rows: rows.filter((r) => matches(r, clauses)), errors };
}
